import type { components } from '@connected/api-client/api.generated';

import { Button } from '@connected/ui/components/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@connected/ui/components/collapsible';
import { Separator } from '@connected/ui/components/separator';
import { Link } from '@tanstack/react-router';
import { ChevronsUpDown, SquareArrowOutUpRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkToValidation, validationLinks } from '../utils/validation-links';

export function ValidationErrorItem({ itemError }: { itemError: components['schemas']['ElectricalProjectValidationResult'] }) {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation('validation');

    return (
        <Collapsible
            open={open}
            onOpenChange={setOpen}
            className="flex w-full flex-col gap-2"
        >
            <div className="flex items-center justify-between gap-1 bg-background-tertiary p-2 rounded-md">
                <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="icon" className="w-full justify-between px-2">
                        <h4 className="text-sm font-semibold">
                            {t(`validation.objectkind.${itemError.ObjectKind}`)}
                        </h4>
                        <ChevronsUpDown />
                    </Button>
                </CollapsibleTrigger>
                {
                    itemError?.ObjectKind && itemError.ObjectKind in validationLinks
                    && (
                        <Button asChild>
                            <Link
                                to={LinkToValidation(itemError)}
                                params={{
                                    projectVersionId: itemError.ElectricalProjectVersionId,
                                    distributionboardId: itemError?.DistributionBoard?.ElectricalProjectDistributionBoardId,
                                    distributionBoardDeviceId: itemError?.DistributionBoardDevice?.ElectricalProjectDistributionBoardDeviceId,
                                    finalCircuitId: itemError?.FinalCircuit?.ElectricalProjectFinalCircuitId,
                                }}
                            >
                                <SquareArrowOutUpRight />
                            </Link>
                        </Button>
                    )

                }

            </div>
            <CollapsibleContent className="flex flex-row gap-4 ps-4 h-full">
                <div>
                    <Separator orientation="vertical" className="h-full" />
                </div>
                <div className="flex flex-col gap-2 w-full">
                    {itemError?.Errors?.map(error => (

                        <div
                            key={`${error.Field || 'field'}-${error.Kind || 'kind'}-${error.Severity || 'severity'}`}
                            className="rounded-md border px-4 py-2 font-mono text-sm"
                            style={{
                                borderColor: error.Severity === 'Error' ? 'red' : error.Severity === 'Warning' ? 'orange' : 'green',
                            }}
                        >
                            {error.Field && !itemError.CheckListItem && t(`validation.errors.fields.${error.Field}`)}
                            {itemError.CheckListItem && itemError.CheckListItem.Description}
                            {' '}
                            (
                            {t(`validation.errors.kind.${error.Kind}`)}
                            )
                        </div>
                    ))}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}
